import type { ColorwayOption } from '@apps/card/routes/CardCustomizer/CardCustomizer.types';
import { swatchColorFromColorway } from '@apps/card/utils/colorway';
import { useTranslation } from '@shared/core';
import { Box, Flex, TextV2, TooltipV2 } from '@withjoy/joykit';
import React from 'react';

type Props = Readonly<{
  options: ReadonlyArray<ColorwayOption>;
  selectedId: string;
  onSelect: (id: string) => void;
  size?: SwatchSize;
  /**
   * 'wrap' (default): render every swatch, wrapping to the next line.
   * 'overflow': clamp to MAX_VISIBLE_SWATCHES and append a multicolor swatch
   * (conic gradient of the hidden colorways) that fires `onOverflowClick`.
   */
  variant?: 'wrap' | 'overflow';
  onOverflowClick?: () => void;
}>;

const SWATCH_SIZE = { small: 20, large: 38 } as const;

type SwatchSize = keyof typeof SWATCH_SIZE;

export const MAX_VISIBLE_SWATCHES = 4;

const overflowGradient = (colorways: ReadonlyArray<string[]>): string => {
  const segment = 360 / colorways.length;
  const stops = colorways.map((values, index) => `${swatchColorFromColorway(values)} ${(index * segment).toFixed(1)}deg ${((index + 1) * segment).toFixed(1)}deg`).join(',');
  return `conic-gradient(from -90deg,${stops})`;
};

export const CardColorSwatches = ({ options, selectedId, onSelect, size = 'small', variant = 'wrap', onOverflowClick }: Props) => {
  const { tq } = useTranslation('stationery');
  const swatchSize = SWATCH_SIZE[size];
  // Collapse the overflow into the multicolor chip only when it stands in for
  // 2+ hidden colors. A single hidden colorway would render as a plain-looking
  // swatch that isn't selectable (it navigates to the PDP), which is
  // indistinguishable from a normal swatch — so in that case show them all.
  const isClamped = variant === 'overflow' && !!onOverflowClick && options.length > MAX_VISIBLE_SWATCHES + 1;
  const visibleOptions = isClamped ? options.slice(0, MAX_VISIBLE_SWATCHES) : options;
  const hiddenColorways = isClamped ? options.slice(MAX_VISIBLE_SWATCHES).map(o => o.values) : [];
  return (
    <Flex
      flexDirection="row"
      alignItems="center"
      gap={3}
      flexWrap={isClamped ? undefined : 'wrap'}
      onClick={e => e.stopPropagation()}
    >
      {visibleOptions.map(({ id, values }) => {
        const color = swatchColorFromColorway(values);
        const isSelected = id === selectedId;
        return (
          <Box
            key={id}
            as="button"
            type="button"
            aria-label={id}
            aria-pressed={isSelected}
            onClick={() => onSelect(id)}
            width={`${swatchSize}px`}
            height={`${swatchSize}px`}
            borderRadius="50%"
            flexShrink={0}
            cursor="pointer"
            border="none"
            padding={0}
            style={{
              backgroundColor: color,
              boxShadow: isSelected ? `0 0 0 2px white, 0 0 0 4px black` : '0 0 0 1px rgba(0,0,0,0.12)',
            }}
          />
        );
      })}
      {isClamped && (
        <TooltipV2
          content={<TextV2>{tq`View all colors`}</TextV2>}
          placement="top"
        >
          <Box
            as="button"
            type="button"
            aria-label={tq`View all colors`}
            onClick={onOverflowClick}
            width={`${swatchSize}px`}
            height={`${swatchSize}px`}
            borderRadius="50%"
            flexShrink={0}
            cursor="pointer"
            border="none"
            padding={0}
            style={{
              background: overflowGradient(hiddenColorways),
              boxShadow: '0 0 0 1px rgba(0,0,0,0.18), inset 0 0 0 1.5px white',
            }}
          />
        </TooltipV2>
      )}
    </Flex>
  );
};
